import {
  countWords,
  insideAny,
  isCapitalizedMidSentence,
  isSentenceStart,
  phraseRegex,
  quotedSpans,
  ratePer,
  type Span,
  splitSentences,
  withoutOverlaps,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Conversational vocabulary that reads as informal in an academic paper, with formal
 * alternatives: "a lot of" → many, "find out" → determine, "kids" → children.
 *
 * What it leaves to other tools, so the same word is never flagged twice for two
 * different reasons: filler words (very, really, just, basically, actually…) are the
 * Filler Word Finder's, contractions the Contraction Expander's, "I" and "you" the
 * First-Person Checker's, -ly adverbs the Adverb Finder's, clichés the Cliché Finder's
 * and padded phrases ("due to the fact that") the Wordiness Checker's.
 *
 * Words with a formal sense are only flagged in their informal one: "kind of" as a
 * hedge ("kind of difficult") but not as a type ("a kind of protein"); "pretty" as an
 * adverb ("pretty large") but not after "a"; "plus" joining clauses but not in "plus or
 * minus"; "like" only as a filler between commas. Quoted speech is skipped — a
 * participant's own words are data. Exclamation marks and questions are counted
 * separately: research questions are fine; rhetorical ones usually read better as
 * statements.
 */

export type InformalKind =
  | "vocabulary"
  | "phrasal"
  | "vague"
  | "spoken"
  | "intensifier"
  | "exclamation"
  | "question";

/** Keys into the widget's `notes` strings, for the words that need a usage note. */
export type InformalNote =
  | "anyway"
  | "get"
  | "kindOf"
  | "like"
  | "lookAt"
  | "plus"
  | "pretty"
  | "vague";

type ContextCheck = "kindOf" | "pretty" | "plus" | "like" | "ok";

interface InformalRule {
  kind: Exclude<InformalKind, "exclamation" | "question">;
  phrases: readonly string[];
  alternatives: readonly string[];
  note?: InformalNote;
  /** Skip inside a name: capitalized mid-sentence, or before a capital ("Big Five", "Georgia Tech"). */
  skipCapitalized?: boolean;
  /** Skip after these words ("a way more…"). */
  notAfter?: readonly string[];
  /** Skip before these words ("big data"). */
  notBefore?: readonly string[];
  check?: ContextCheck;
}

/** "find out" in every form: "finds out", "found out", "finding out". */
function phrasal(forms: readonly string[], particle: string): string[] {
  return forms.map((form) => `${form} ${particle}`);
}

const FIND = ["find", "finds", "found", "finding"];
const LOOK = ["look", "looks", "looked", "looking"];
const COME = ["come", "comes", "came", "coming"];
const GET = ["get", "gets", "got", "getting", "gotten"];
const GO = ["go", "goes", "went", "gone", "going"];
const PUT = ["put", "puts", "putting"];
const MAKE = ["make", "makes", "made", "making"];

const VERB_RULES: readonly InformalRule[] = [
  {
    kind: "phrasal",
    phrases: phrasal(FIND, "out"),
    alternatives: ["determine", "discover", "establish"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(LOOK, "into"),
    alternatives: ["investigate", "examine"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(LOOK, "at"),
    alternatives: ["examine", "consider", "analyze"],
    note: "lookAt",
  },
  {
    kind: "phrasal",
    phrases: phrasal(COME, "up with"),
    alternatives: ["develop", "devise", "propose"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(COME, "across"),
    alternatives: ["encounter", "find", "seem"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(GET, "rid of"),
    alternatives: ["eliminate", "remove"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(GET, "across"),
    alternatives: ["convey", "communicate"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(GET, "over"),
    alternatives: ["recover from", "overcome"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["figure", "figures", "figured", "figuring"], "out"),
    alternatives: ["determine", "work out", "solve"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["talk", "talks", "talked", "talking"], "about"),
    alternatives: ["discuss", "address"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(GO, "up"),
    alternatives: ["increase", "rise"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(GO, "down"),
    alternatives: ["decrease", "decline", "fall"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(GO, "over"),
    alternatives: ["review", "examine", "exceed"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["deal", "deals", "dealt", "dealing"], "with"),
    alternatives: ["address", "manage", "handle"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(PUT, "up with"),
    alternatives: ["tolerate", "accept"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(PUT, "off"),
    alternatives: ["postpone", "delay", "deter"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["check", "checks", "checked", "checking"], "out"),
    alternatives: ["examine", "verify"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["turn", "turns", "turned", "turning"], "down"),
    alternatives: ["reject", "decline"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["bring", "brings", "brought", "bringing"], "up"),
    alternatives: ["raise", "mention", "introduce"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["back", "backs", "backed", "backing"], "up"),
    alternatives: ["support", "confirm"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["cut", "cuts", "cutting"], "down on"),
    alternatives: ["reduce"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["end", "ends", "ended", "ending"], "up"),
    alternatives: ["eventually", "ultimately", "finally"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["leave", "leaves", "left", "leaving"], "out"),
    alternatives: ["omit", "exclude"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["show", "shows", "showed", "shown", "showing"], "up"),
    alternatives: ["appear", "attend", "arrive"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["sort", "sorts", "sorted", "sorting"], "out"),
    alternatives: ["resolve", "organize"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["work", "works", "worked", "working"], "out"),
    alternatives: ["calculate", "determine", "resolve"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["mess", "messes", "messed", "messing"], "up"),
    alternatives: ["disrupt", "damage", "spoil"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["give", "gives", "gave", "given", "giving"], "up"),
    alternatives: ["abandon", "stop", "relinquish"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["keep", "keeps", "kept", "keeping"], "up with"),
    alternatives: ["keep pace with", "maintain"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["pick", "picks", "picked", "picking"], "up"),
    alternatives: ["acquire", "collect", "learn"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(["speed", "speeds", "sped", "speeded", "speeding"], "up"),
    alternatives: ["accelerate"],
  },
  {
    kind: "phrasal",
    phrases: phrasal(MAKE, "up"),
    alternatives: ["constitute", "comprise", "compensate for"],
  },
  {
    kind: "vocabulary",
    phrases: GET,
    alternatives: ["obtain", "receive", "become", "understand"],
    note: "get",
  },
];

const WORD_RULES: readonly InformalRule[] = [
  {
    kind: "vocabulary",
    phrases: ["a lot of", "lots of", "a whole lot of"],
    alternatives: ["many", "much", "a great deal of", "considerable"],
  },
  {
    kind: "vocabulary",
    phrases: ["a lot", "a whole lot"],
    alternatives: ["considerably", "substantially", "much"],
  },
  {
    kind: "vocabulary",
    phrases: ["tons of", "a ton of", "loads of", "a load of", "heaps of"],
    alternatives: ["many", "a great deal of", "a large amount of"],
  },
  {
    kind: "vocabulary",
    phrases: ["a bunch of", "bunch of"],
    alternatives: ["several", "many", "a group of"],
  },
  {
    kind: "vocabulary",
    phrases: ["a couple of"],
    alternatives: ["two", "a few", "several"],
  },
  {
    kind: "vocabulary",
    phrases: ["kids", "kid"],
    alternatives: ["children", "child"],
    skipCapitalized: true,
  },
  {
    kind: "vocabulary",
    phrases: ["guys", "guy"],
    alternatives: ["people", "men", "man"],
    skipCapitalized: true,
  },
  { kind: "vocabulary", phrases: ["folks"], alternatives: ["people"] },
  {
    kind: "vocabulary",
    phrases: ["moms", "mom", "mums", "mum"],
    alternatives: ["mother", "mothers"],
  },
  {
    kind: "vocabulary",
    phrases: ["dads", "dad"],
    alternatives: ["father", "fathers"],
  },
  {
    kind: "vocabulary",
    phrases: ["big"],
    alternatives: ["large", "substantial", "major"],
    skipCapitalized: true,
    notBefore: ["data"],
  },
  {
    kind: "vocabulary",
    phrases: ["huge"],
    alternatives: ["considerable", "substantial", "very large"],
  },
  {
    kind: "vocabulary",
    phrases: ["awesome"],
    alternatives: ["impressive", "excellent"],
  },
  {
    kind: "vocabulary",
    phrases: ["amazing"],
    alternatives: ["remarkable", "striking"],
  },
  {
    kind: "vocabulary",
    phrases: ["incredible"],
    alternatives: ["remarkable", "extraordinary"],
  },
  {
    kind: "vocabulary",
    phrases: ["nowadays"],
    alternatives: ["currently", "today", "at present"],
  },
  { kind: "vocabulary", phrases: ["till"], alternatives: ["until"] },
  {
    kind: "vocabulary",
    phrases: ["tricky"],
    alternatives: ["difficult", "complex"],
  },
  {
    kind: "vocabulary",
    phrases: ["nasty"],
    alternatives: ["harmful", "severe", "unpleasant"],
  },
  {
    kind: "vocabulary",
    phrases: ["ridiculous"],
    alternatives: ["unreasonable", "absurd"],
  },
  { kind: "vocabulary", phrases: ["info"], alternatives: ["information"] },
  { kind: "vocabulary", phrases: ["stats"], alternatives: ["statistics"] },
  {
    kind: "vocabulary",
    phrases: ["tech"],
    alternatives: ["technology"],
    skipCapitalized: true,
  },
  { kind: "vocabulary", phrases: ["ads"], alternatives: ["advertisements"] },
  {
    kind: "vocabulary",
    phrases: ["alright"],
    alternatives: ["all right", "acceptable"],
  },
  {
    kind: "vocabulary",
    phrases: ["okay", "ok"],
    alternatives: ["acceptable", "satisfactory", "adequate"],
    check: "ok",
  },
  {
    kind: "vocabulary",
    phrases: ["anyway", "anyways", "anyhow"],
    alternatives: ["in any case", "regardless"],
    note: "anyway",
  },
  { kind: "spoken", phrases: ["gonna"], alternatives: ["going to", "will"] },
  { kind: "spoken", phrases: ["wanna"], alternatives: ["want to"] },
  { kind: "spoken", phrases: ["gotta"], alternatives: ["have to", "must"] },
  {
    kind: "spoken",
    phrases: ["kinda", "sorta"],
    alternatives: ["somewhat", "rather"],
  },
  { kind: "spoken", phrases: ["dunno"], alternatives: ["do not know"] },
  { kind: "spoken", phrases: ["lemme"], alternatives: ["let me"] },
  {
    kind: "spoken",
    phrases: ["'cause", "cuz", "coz"],
    alternatives: ["because"],
  },
  { kind: "spoken", phrases: ["yeah", "yep", "yup"], alternatives: ["yes"] },
  { kind: "spoken", phrases: ["nope"], alternatives: ["no"] },
  {
    kind: "spoken",
    phrases: ["for sure"],
    alternatives: ["certainly", "definitely"],
  },
  {
    kind: "spoken",
    phrases: ["kind of", "sort of"],
    alternatives: ["somewhat", "rather", "partly"],
    note: "kindOf",
    check: "kindOf",
  },
  {
    kind: "spoken",
    phrases: ["pretty much"],
    alternatives: ["almost", "largely", "nearly"],
  },
  {
    kind: "intensifier",
    phrases: ["pretty"],
    alternatives: ["fairly", "rather", "quite"],
    note: "pretty",
    check: "pretty",
  },
  {
    kind: "spoken",
    phrases: ["plus"],
    alternatives: ["in addition", "moreover", "and"],
    note: "plus",
    check: "plus",
  },
  {
    kind: "spoken",
    phrases: ["like"],
    alternatives: ["such as", "for example"],
    note: "like",
    check: "like",
  },
  {
    kind: "intensifier",
    phrases: ["super"],
    alternatives: ["very", "extremely", "highly"],
    skipCapitalized: true,
  },
  {
    kind: "intensifier",
    phrases: ["awfully", "terribly"],
    alternatives: ["very", "extremely", "severely"],
  },
  {
    kind: "intensifier",
    phrases: [
      "way better",
      "way bigger",
      "way fewer",
      "way higher",
      "way larger",
      "way less",
      "way lower",
      "way more",
      "way smaller",
      "way too",
      "way worse",
    ],
    alternatives: ["far more", "much more", "considerably"],
    notAfter: [
      "a",
      "another",
      "any",
      "each",
      "every",
      "no",
      "one",
      "other",
      "same",
      "that",
      "the",
      "this",
    ],
  },
  {
    kind: "vague",
    phrases: ["stuff"],
    alternatives: ["materials", "items", "content"],
    note: "vague",
  },
  {
    kind: "vague",
    phrases: ["things", "thing"],
    alternatives: ["factors", "aspects", "items"],
    note: "vague",
  },
];

/** Before "kind of" / "sort of", these make it mean "type of", which is formal. */
const TYPE_DETERMINERS = new Set([
  "a",
  "all",
  "an",
  "another",
  "any",
  "certain",
  "different",
  "each",
  "every",
  "first",
  "her",
  "his",
  "its",
  "many",
  "my",
  "new",
  "no",
  "one",
  "only",
  "other",
  "our",
  "particular",
  "right",
  "same",
  "second",
  "similar",
  "some",
  "specific",
  "such",
  "that",
  "the",
  "their",
  "these",
  "this",
  "those",
  "what",
  "whatever",
  "which",
  "whichever",
  "wrong",
  "your",
]);

/** Before "pretty", these make it the adjective ("a pretty garden"). */
const ADJECTIVE_CONTEXT = new Set([
  "a",
  "an",
  "as",
  "her",
  "his",
  "how",
  "its",
  "my",
  "our",
  "quite",
  "rather",
  "so",
  "that",
  "the",
  "their",
  "this",
  "too",
  "very",
  "your",
]);

export interface InformalMatch {
  start: number;
  end: number;
  text: string;
  kind: InformalKind;
  /** Lowercase, single-spaced: the row a match is tallied under. */
  key: string;
  alternatives: readonly string[];
  note?: InformalNote;
}

export interface InformalRow {
  key: string;
  kind: InformalKind;
  count: number;
  alternatives: readonly string[];
  note?: InformalNote;
}

export interface InformalReport {
  words: number;
  matches: InformalMatch[];
  /** Informal words and spoken forms (everything but phrasal verbs and punctuation). */
  informal: number;
  phrasal: number;
  exclamations: number;
  questions: number;
  /** Informal words and phrasal verbs per 1,000 words. */
  per1000: number;
  /** One row per word or phrase, most frequent first. Punctuation excluded. */
  rows: InformalRow[];
  /** Matches inside quotation marks, left alone. */
  quotedSkipped: number;
}

const COMPILED = [...VERB_RULES, ...WORD_RULES].map((rule) => ({
  rule,
  regex: phraseRegex(rule.phrases),
}));

const RE_WORD = /[\p{L}][\p{L}'’-]*/gu;
const RE_SPACES = /\s+/g;
const RE_CURLY = /’/g;
const RE_EXCLAMATION = /!+(?!=)/g;
const RE_QUESTION_END = /\?["'”’)\]]*$/;
const RE_LAST_NON_SPACE = /\S(?=\s*$)/;
const RE_FIRST_NON_SPACE = /^\s*(\S)/;
const RE_NEXT_WORD_RAW = /^\s+(\p{L}+)/u;
const RE_UPPER_START = /^\p{Lu}/u;

function keyOf(text: string): string {
  return text.toLowerCase().replace(RE_CURLY, "'").replace(RE_SPACES, " ");
}

function previousWord(text: string, start: number): string {
  const words = [
    ...text.slice(Math.max(0, start - 40), start).matchAll(RE_WORD),
  ];
  return words.at(-1)?.[0].toLowerCase() ?? "";
}

function nextWords(text: string, end: number, count: number): string[] {
  return [...text.slice(end, end + 40).matchAll(RE_WORD)]
    .slice(0, count)
    .map((m) => m[0].toLowerCase());
}

function previousChar(text: string, start: number): string {
  return (
    RE_LAST_NON_SPACE.exec(text.slice(Math.max(0, start - 8), start))?.[0] ?? ""
  );
}

function nextChar(text: string, end: number): string {
  return RE_FIRST_NON_SPACE.exec(text.slice(end, end + 8))?.[1] ?? "";
}

function passesCheck(
  text: string,
  check: ContextCheck,
  start: number,
  end: number
): boolean {
  switch (check) {
    case "kindOf":
      return !TYPE_DETERMINERS.has(previousWord(text, start));
    case "pretty":
      return !ADJECTIVE_CONTEXT.has(previousWord(text, start));
    case "plus": {
      const minus = nextWords(text, end, 2).join(" ");
      if (minus === "or minus" || minus.startsWith("minus")) {
        return false;
      }
      return isSentenceStart(text, start) || previousChar(text, start) === ",";
    }
    case "like":
      // "It was, like, fine" or "Like, the results…": the filler sits between commas.
      return (
        nextChar(text, end) === "," &&
        (previousChar(text, start) === "," || isSentenceStart(text, start))
      );
    case "ok":
      // "Tulsa, OK" is a state abbreviation.
      return !(
        text.slice(start, end) === "OK" && previousChar(text, start) === ","
      );
    default:
      return true;
  }
}

function partOfName(text: string, start: number, end: number): boolean {
  if (isCapitalizedMidSentence(text, start)) {
    return true;
  }
  const next = RE_NEXT_WORD_RAW.exec(text.slice(end, end + 24))?.[1] ?? "";
  return RE_UPPER_START.test(next);
}

function applies(
  text: string,
  rule: InformalRule,
  start: number,
  end: number
): boolean {
  if (rule.skipCapitalized && partOfName(text, start, end)) {
    return false;
  }
  if (rule.notAfter?.includes(previousWord(text, start))) {
    return false;
  }
  if (rule.notBefore?.includes(nextWords(text, end, 1)[0] ?? "")) {
    return false;
  }
  return !rule.check || passesCheck(text, rule.check, start, end);
}

function wordMatches(
  text: string,
  quoted: readonly Span[]
): { matches: InformalMatch[]; skipped: number } {
  const found: InformalMatch[] = [];
  let skipped = 0;
  for (const { rule, regex } of COMPILED) {
    for (const hit of text.matchAll(regex)) {
      const start = hit.index;
      const end = start + hit[0].length;
      if (!applies(text, rule, start, end)) {
        continue;
      }
      if (insideAny(quoted, start, end)) {
        skipped += 1;
        continue;
      }
      found.push({
        start,
        end,
        text: hit[0],
        kind: rule.kind,
        key: keyOf(hit[0]),
        alternatives: rule.alternatives,
        note: rule.note,
      });
    }
  }
  return { matches: found, skipped };
}

function exclamationMatches(
  text: string,
  quoted: readonly Span[]
): InformalMatch[] {
  return [...text.matchAll(RE_EXCLAMATION)]
    .filter((hit) => !insideAny(quoted, hit.index, hit.index + hit[0].length))
    .map((hit) => ({
      start: hit.index,
      end: hit.index + hit[0].length,
      text: hit[0],
      kind: "exclamation" as const,
      key: "!",
      alternatives: [],
    }));
}

/** A question is marked on its question mark, so it never hides a word match. */
function questionMatches(
  text: string,
  quoted: readonly Span[]
): InformalMatch[] {
  const matches: InformalMatch[] = [];
  for (const sentence of splitSentences(text)) {
    if (
      !RE_QUESTION_END.test(sentence.text) ||
      insideAny(quoted, sentence.start, sentence.end)
    ) {
      continue;
    }
    const start = sentence.start + sentence.text.lastIndexOf("?");
    matches.push({
      start,
      end: start + 1,
      text: "?",
      kind: "question",
      key: "?",
      alternatives: [],
    });
  }
  return matches;
}

function rowsFor(matches: readonly InformalMatch[]): InformalRow[] {
  const rows = new Map<string, InformalRow>();
  for (const match of matches) {
    if (match.kind === "exclamation" || match.kind === "question") {
      continue;
    }
    const row = rows.get(match.key);
    if (row) {
      row.count += 1;
    } else {
      rows.set(match.key, {
        key: match.key,
        kind: match.kind,
        count: 1,
        alternatives: match.alternatives,
        note: match.note,
      });
    }
  }
  return [...rows.values()].sort(
    (a, b) => b.count - a.count || a.key.localeCompare(b.key)
  );
}

export function findInformalLanguage(text: string): InformalReport {
  const quoted = quotedSpans(text);
  const words = wordMatches(text, quoted);
  const matches = [
    ...withoutOverlaps(words.matches),
    ...exclamationMatches(text, quoted),
    ...questionMatches(text, quoted),
  ].sort((a, b) => a.start - b.start);
  const phrasalCount = matches.filter((m) => m.kind === "phrasal").length;
  const exclamations = matches.filter((m) => m.kind === "exclamation").length;
  const questions = matches.filter((m) => m.kind === "question").length;
  const informal = matches.length - phrasalCount - exclamations - questions;
  const wordCount = countWords(text);
  return {
    words: wordCount,
    matches,
    informal,
    phrasal: phrasalCount,
    exclamations,
    questions,
    per1000: ratePer(informal + phrasalCount, wordCount, 1000),
    rows: rowsFor(matches),
    quotedSkipped: words.skipped,
  };
}
